import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiLock, FiShield } from 'react-icons/fi';

type LegalPageType = 'privacy' | 'terms';

interface LegalPageProps {
  type: LegalPageType;
}

const updatedDate = 'June 30, 2026';

const sectionsByType: Record<LegalPageType, Array<{ title: string; body: string[] }>> = {
  privacy: [
    {
      title: 'Information we collect',
      body: [
        'Whizpr collects the information needed to create and protect your account, such as your name, email address, profile photo if provided by your sign-in provider, and authentication identifiers.',
        'When you use Whizpr, we process messages, media you choose to send, friend connections, invitation links, basic device and browser details, and service logs needed to operate, secure, and troubleshoot the app.',
      ],
    },
    {
      title: 'How we use information',
      body: [
        'We use information to sign you in, deliver messages, show your conversations and friend list, prevent abuse, maintain the service, and improve reliability.',
        'We do not sell personal information. We do not use your private conversations for advertising.',
      ],
    },
    {
      title: 'Google sign-in',
      body: [
        'If you choose Google sign-in, Whizpr uses Google account information only for authentication and account setup. Access is limited to the scopes shown on the Google consent screen.',
      ],
    },
    {
      title: 'Sharing and retention',
      body: [
        'We share information only with service providers needed to run Whizpr, when required by law, to protect users and the service, or with your direction.',
        'We retain account and conversation data while your account is active or as needed to provide the service, comply with legal obligations, resolve disputes, and enforce agreements.',
      ],
    },
    {
      title: 'Your choices',
      body: [
        'You can stop using Whizpr at any time and may request account or data deletion by contacting support.',
        'You can also manage Google account permissions from your Google Account security settings.',
      ],
    },
    {
      title: 'Contact',
      body: [
        'For privacy questions or data requests, contact us at support@whizpr.chat.',
      ],
    },
  ],
  terms: [
    {
      title: 'Use of Whizpr',
      body: [
        'Whizpr is a private messaging web app. You agree to use it only for lawful purposes and in a way that does not harm other users, the service, or third-party rights.',
        'You are responsible for keeping your account secure and for activity that happens through your account.',
      ],
    },
    {
      title: 'User content',
      body: [
        'You keep ownership of messages, media, and other content you send through Whizpr.',
        'You grant Whizpr the limited permission needed to store, transmit, display, and process your content so the service can function.',
      ],
    },
    {
      title: 'Acceptable conduct',
      body: [
        'Do not use Whizpr to harass others, send unlawful or harmful content, attempt unauthorized access, disrupt service operation, or misuse invitations and authentication systems.',
      ],
    },
    {
      title: 'Service availability',
      body: [
        'We work to keep Whizpr reliable, but the service may change, pause, or be unavailable from time to time. Whizpr is provided without warranties to the fullest extent permitted by law.',
      ],
    },
    {
      title: 'Limitation of liability',
      body: [
        'To the fullest extent permitted by law, Whizpr and its operators are not liable for indirect, incidental, consequential, special, or punitive damages arising from use of the service.',
      ],
    },
    {
      title: 'Contact',
      body: [
        'For questions about these terms, contact us at support@whizpr.chat.',
      ],
    },
  ],
};

const pageCopy = {
  privacy: {
    title: 'Privacy Policy',
    intro: 'This Privacy Policy explains how Whizpr collects, uses, and protects information when you use our messaging service.',
    icon: FiShield,
  },
  terms: {
    title: 'Terms of Service',
    intro: 'These Terms of Service explain the rules for using Whizpr and the responsibilities that come with your account.',
    icon: FiLock,
  },
};

const LegalPage: React.FC<LegalPageProps> = ({ type }) => {
  const copy = pageCopy[type];
  const Icon = copy.icon;

  return (
    <main className="min-h-screen bg-[#09090B] px-5 py-6 text-white">
      <div className="mx-auto w-full max-w-3xl">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#A1A1AA] transition hover:text-white"
        >
          <FiArrowLeft className="h-4 w-4" />
          Back to Whizpr
        </Link>

        <header className="mt-8 border-b border-white/10 pb-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-[#7C3AED]/45 bg-[#7C3AED]/10 text-[#A78BFA]">
            <Icon className="h-6 w-6" />
          </div>
          <h1 className="mt-5 text-3xl font-black tracking-normal text-white sm:text-4xl">
            Whizpr {copy.title}
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-7 text-[#D4D4D8]">{copy.intro}</p>
          <p className="mt-4 text-sm font-semibold text-[#A1A1AA]">Last updated: {updatedDate}</p>
        </header>

        <div className="space-y-8 py-8">
          {sectionsByType[type].map((section) => (
            <section key={section.title}>
              <h2 className="text-xl font-extrabold tracking-normal text-white">{section.title}</h2>
              <div className="mt-3 space-y-3 text-[0.95rem] leading-7 text-[#D4D4D8]">
                {section.body.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </main>
  );
};

export default LegalPage;
